import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Text } from '@/components/ui/Text'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { displaySegmentValueLabel } from '@/lib/utils/column-labels'
import type {
  FilterChipGroup,
  FilterChipItem,
} from '@/types/components/audiences/filter-chip-grid'

interface FilterDetailModalProps {
  item: FilterChipItem | null
  sampleValues: string[]
  open: boolean
  onOpenChange: (open: boolean) => void
  onRename?: (sourceColumn: string, newLabel: string) => void
  /** When set, the modal renders a grouped-filter view (options ↔ sample values). */
  group?: FilterChipGroup | null
  onRenameGroup?: (groupId: string, newLabel: string) => void
}

export function FilterDetailModal({
  item,
  sampleValues,
  open,
  onOpenChange,
  onRename,
  group,
  onRenameGroup,
}: FilterDetailModalProps) {
  const { t } = useTranslation('app')
  const [editName, setEditName] = useState('')

  /** Deduped set of all answer values across the group's options (display labels). */
  const groupUniqueValues = useMemo(() => {
    if (!group) {
      return []
    }
    const seen = new Set<string>()
    const out: Array<string> = []
    for (const opt of group.options) {
      for (const raw of opt.sampleValues) {
        const label = displaySegmentValueLabel(
          { valueLabels: opt.valueLabels, valueMap: opt.valueMap },
          raw,
        )
        if (!seen.has(label)) {
          seen.add(label)
          out.push(label)
        }
      }
    }
    return out
  }, [group])

  useEffect(() => {
    if (open) {
      if (group) {
        setEditName(group.label)
      } else if (item) {
        setEditName(item.displayLabel)
      }
    }
  }, [item, group, open])

  if (group) {
    // Allow empty string — user clearing the alias reverts to the auto-derived stem.
    const groupNameChanged = editName.trim() !== group.label

    const handleGroupSave = () => {
      if (groupNameChanged && onRenameGroup) {
        onRenameGroup(group.groupId, editName.trim())
      }
      onOpenChange(false)
    }

    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display font-normal text-[24px] truncate">
              {editName.trim() || group.label}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {t('audienceCreate.groupedFilterDetail')}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-2">
            <label htmlFor="group-detail-name">
              <Text size="xs" color="muted" as="span" className="text-[14px]">
                {t('audienceCreate.filterName')}
              </Text>
            </label>
            <Input
              id="group-detail-name"
              className="text-[16px]"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
            />
          </div>

          <TooltipProvider delayDuration={300}>
            <div className="grid min-w-0 grid-cols-2 gap-3">
              {/* Options column — one row per option */}
              <div className="flex min-w-0 flex-col gap-1.5">
                <Text size="xs" color="muted" className="text-[14px]">
                  {t('audienceCreate.options')}
                </Text>
                <ScrollArea className="h-[280px] rounded-md border border-border/40">
                  <div className="flex flex-col gap-0.5 p-2">
                    {group.options.map((opt) => (
                      <Tooltip key={opt.sourceColumn}>
                        <TooltipTrigger asChild>
                          <Text
                            size="sm"
                            className="block min-w-0 cursor-default truncate py-0.5 text-[15px]"
                          >
                            {opt.optionLabel}
                          </Text>
                        </TooltipTrigger>
                        <TooltipContent side="top" align="start" className="max-w-sm text-xs">
                          {opt.optionLabel}
                        </TooltipContent>
                      </Tooltip>
                    ))}
                  </div>
                </ScrollArea>
              </div>

              {/* Values column — deduped set of all answers */}
              <div className="flex min-w-0 flex-col gap-1.5">
                <Text size="xs" color="muted" className="text-[14px]">
                  {t('audienceCreate.values')}
                </Text>
                <ScrollArea className="h-[280px] rounded-md border border-border/40">
                  <div className="flex flex-wrap content-start gap-1.5 p-2">
                    {groupUniqueValues.length > 0 ? (
                      groupUniqueValues.map((value) => (
                        <Tooltip key={value}>
                          <TooltipTrigger asChild>
                            <Badge
                              variant="outline"
                              className="max-w-full cursor-default text-sm font-normal"
                            >
                              <span className="min-w-0 truncate">{value}</span>
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent side="top" align="start" className="max-w-sm text-xs">
                            {value}
                          </TooltipContent>
                        </Tooltip>
                      ))
                    ) : (
                      <Text size="xs" color="muted">
                        {t('audienceCreate.noSampleValues')}
                      </Text>
                    )}
                  </div>
                </ScrollArea>
              </div>
            </div>
          </TooltipProvider>

          <DialogFooter className="sm:justify-center">
            <Button
              variant="primary"
              className="text-[16px]"
              disabled={!groupNameChanged}
              onClick={handleGroupSave}
            >
              {t('audienceCreate.saveChanges')}
            </Button>
            <Button
              variant="secondary"
              className="text-[16px]"
              onClick={() => onOpenChange(false)}
            >
              {t('audienceCreate.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  if (!item) return null

  const nameChanged = editName.trim() !== '' && editName.trim() !== item.displayLabel

  function handleSave() {
    if (!item) {
      return
    }
    if (nameChanged && onRename) {
      onRename(item.sourceColumn, editName.trim())
    }
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" style={{ maxWidth: 'fit-content' }}>
        <DialogHeader>
          <DialogTitle className="font-display font-normal text-[24px]">
            {item.displayLabel}
          </DialogTitle>
          {item.originalLabel && item.originalLabel !== item.displayLabel ? (
            <p className="truncate pt-0.5 font-sans text-[16px] text-muted-foreground">
              {item.originalLabel}
            </p>
          ) : null}
          <DialogDescription className="sr-only">
            {t('audienceCreate.filterDetail')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2">
          <label htmlFor="detail-filter-name">
            <Text size="xs" color="muted" as="span" className="text-[14px]">
              {t('audienceCreate.filterName')}
            </Text>
          </label>
          <Input
            id="detail-filter-name"
            className="text-[16px]"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
          />
        </div>

        {sampleValues.length > 0 ? (
          <div className="flex flex-col gap-1.5">
            <Text size="xs" color="muted" className="text-[14px]">
              {t('audienceCreate.sampleValues')}
            </Text>
            <ScrollArea className="max-h-[240px]">
              <div className="flex flex-wrap gap-1.5 p-1">
                {sampleValues.map((value) => (
                  <Badge key={value} variant="outline" className="text-sm font-normal">
                    {displaySegmentValueLabel({ valueLabels: item.valueLabels, valueMap: item.valueMap }, value)}
                  </Badge>
                ))}
              </div>
            </ScrollArea>
          </div>
        ) : (
          <div className="flex items-center justify-center rounded-xl border border-dashed border-border/70 px-4 py-8">
            <Text size="sm" color="muted">
              {t('audienceCreate.noSampleValuesAvailable')}
            </Text>
          </div>
        )}

        <DialogFooter className="sm:justify-center">
          <Button variant="primary" className="text-[16px]" disabled={!nameChanged} onClick={handleSave}>
            {t('audienceCreate.saveChanges')}
          </Button>
          <Button variant="secondary" className="text-[16px]" onClick={() => onOpenChange(false)}>
            {t('audienceCreate.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
